import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Play, ArrowRight } from 'lucide-react';
import VideoCaseStudyModal from './VideoCaseStudyModal';
import { FLYT_CATEGORIES } from '../data/flytCategories';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveImg = (url) => (url && url.startsWith('/api/')) ? `${BACKEND_URL}${url}` : url;

/**
 * CaseStudiesGrid — FLYT homepage section.
 * 6 case study tiles in a 2×3 / 3×2 grid (rounded square boxes). Click opens
 * the same VideoCaseStudyModal used on /flyt/process — ideation, goal,
 * approach + video. "See All" button links to /flyt/case-studies.
 */
const CaseStudiesGrid = ({ limit = 6 }) => {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [activeCat, setActiveCat] = useState('All');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/flyt/examples`)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
  }, []);

  // Only show categories that have at least one case study (keeps chips relevant)
  const availableCats = useMemo(() => {
    const present = new Set(items.map(i => i.category).filter(Boolean));
    return ['All', ...FLYT_CATEGORIES.filter(c => present.has(c))];
  }, [items]);

  const filtered = useMemo(() => {
    const base = activeCat === 'All' ? items : items.filter(i => i.category === activeCat);
    return base.slice(0, limit);
  }, [items, activeCat, limit]);

  if (!loaded) return null;
  if (items.length === 0) return null;

  return (
    <section className="relative pt-4 pb-20 lg:pt-6 lg:pb-28 bg-black overflow-hidden -mt-1" data-testid="case-studies-grid">
      {/* Soft yellow glow background */}
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 12% 18%, rgba(212,175,55,0.35), transparent 55%), radial-gradient(circle at 88% 82%, rgba(240,216,117,0.28), transparent 55%)',
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header merged with the tagline above — keep only a thin
            "The work behind the stories" eyebrow as a visual handoff,
            then drop straight into the grid. */}
        <p className="font-italic text-yellow-300 text-xs sm:text-sm tracking-[0.22em] uppercase mb-6 lg:mb-8" data-testid="case-studies-eyebrow">
          The work
        </p>

        {/* 6-tile grid */}
        {filtered.length === 0 ? (
          <p className="text-gray-400 italic py-12 text-center">No case studies in this category yet.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5 lg:gap-6">
            {filtered.map((it, idx) => (
              <button
                key={it.id}
                type="button"
                onClick={() => setSelected(it)}
                className="group relative aspect-square rounded-2xl lg:rounded-3xl overflow-hidden bg-[#0a0a0a] ring-1 ring-white/10 hover:ring-yellow-300/60 transition-all hover:-translate-y-1 hover:shadow-[0_20px_60px_-20px_rgba(240,216,117,0.45)] text-left"
                data-testid={`case-study-tile-${idx}`}
                aria-label={`Open case study: ${it.title}`}
              >
                {it.poster ? (
                  <img
                    src={resolveImg(it.poster)}
                    alt={it.title}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-black" />
                )}

                {/* Bottom gradient + play badge */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-yellow-300/95 text-black flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                  <Play className="w-4 h-4 fill-black ml-0.5" />
                </div>

                <div className="absolute bottom-0 inset-x-0 p-4 lg:p-5">
                  {it.category && (
                    <p className="font-display text-[10px] uppercase tracking-widest text-yellow-300 mb-1.5 line-clamp-1">{it.category}</p>
                  )}
                  <p className="font-display text-base sm:text-lg lg:text-xl uppercase leading-tight text-white line-clamp-2">{it.title}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* See All */}
        <div className="text-center mt-10 lg:mt-12">
          <Link
            to="/flyt/case-studies"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-yellow-300 hover:bg-yellow-200 text-black font-bold transition group"
            data-testid="case-studies-see-all-btn"
          >
            See All FLYT Built Stories
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
          </Link>
        </div>
      </div>

      {selected && (
        <VideoCaseStudyModal example={selected} onClose={() => setSelected(null)} />
      )}
    </section>
  );
};

export default CaseStudiesGrid;
